import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useNotification } from '../../context/NotificationContext';
import { useSocket } from '../../context/SocketContext';
import { consultationAPI } from '../../utils/api';

const ChatRoom = () => {
    const { consultationId } = useParams();
    const navigate = useNavigate();
    const { user } = useAuth();
    const { showSuccess, showError } = useNotification();
    const { socket } = useSocket(); // Get socket instance
    const [messages, setMessages] = useState([]);
    const [newMessage, setNewMessage] = useState('');
    const [loading, setLoading] = useState(true);
    const messagesEndRef = useRef(null);

    const [messageLimits, setMessageLimits] = useState({
        patientCount: 0,
        providerCount: 0
    });

    const [consultation, setConsultation] = useState(null);
    const [prescription, setPrescription] = useState(null);
    const [showPrescriptionModal, setShowPrescriptionModal] = useState(false);
    const [showHistoryModal, setShowHistoryModal] = useState(false);

    // Prescription State
    const [diagnosis, setDiagnosis] = useState('');
    const [advice, setAdvice] = useState('');
    const [medicines, setMedicines] = useState([{ name: '', dosage: '', frequency: '', duration: '' }]);

    const isPatient = user?.role === 'patient';

    useEffect(() => {
        fetchMessages();
        fetchConsultationDetails();

        // Socket.io Real-time connection
        if (socket && consultationId) {
            socket.emit('join_room', consultationId);

            const handleReceiveMessage = (data) => {
                console.log('Received message:', data);
                // Append new message to state if it matches current room
                if (data.room === consultationId) {
                    setMessages((prev) => [...prev, data.message]);
                    scrollToBottom();
                }
            };

            socket.on('receive_message', handleReceiveMessage);

            return () => {
                socket.off('receive_message', handleReceiveMessage);
                socket.emit('leave_room', consultationId);
            };
        }
        // Use a longer poll interval as fallback or backup
        const interval = setInterval(fetchMessages, 10000);
        return () => clearInterval(interval);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [consultationId, socket]);

    const fetchConsultationDetails = async () => {
        try {
            // We need an endpoint or logic to get the single consultation
            // For now, let's assume getHistory or similar can be used or we add a specific one
            const res = await consultationAPI.getHistory(user?._id || user?.id);
            if (res.data?.success) {
                const list = res.data.data?.items ?? res.data.data;
                const current = list.find(c => (c._id || c.id) === consultationId);
                setConsultation(current);
                if (current?.diagnosis) setDiagnosis(current.diagnosis);
            }
        } catch (error) {
            console.error('Failed to fetch consultation details');
        }
    };

    useEffect(() => {
        scrollToBottom();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [messages]);

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const fetchMessages = async () => {
        try {
            const response = await consultationAPI.getMessages(consultationId); // Ensure this API method exists or add it
            if (response.data.success) {
                setMessages(response.data.data);
                calculateLimits(response.data.data);
                setLoading(false);
            }
        } catch (error) {
            console.error('Failed to load messages:', error);
        }
    };

    const calculateLimits = (msgs) => {
        const patientMsgs = msgs.filter(m => m.sender_role === 'patient').length;
        const providerMsgs = msgs.filter(m => m.sender_role !== 'patient').length;
        setMessageLimits({ patientCount: patientMsgs, providerCount: providerMsgs });
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        if (!newMessage.trim()) return;

        if (newMessage.length > 500) {
            showError('Message exceeds 500 characters');
            return;
        }

        if (isPatient && messageLimits.patientCount >= 10) {
            showError('You have reached the limit of 10 messages.');
            return;
        }
        if (!isPatient && messageLimits.providerCount >= 10) {
            showError('You have reached the limit of 10 messages.');
            return;
        }

        try {
            const response = await consultationAPI.sendMessage(consultationId, { content: newMessage });
            if (response.data.success) {
                // Emit to socket for real-time update
                const savedMsg = response.data.data; // Assuming API returns the created message
                socket.emit('send_message', {
                    room: consultationId,
                    message: savedMsg
                });

                setNewMessage('');
                // fetchMessages(); // No need to fetch if socket updates state, but keeping for safety
            }
        } catch (error) {
            showError(error.response?.data?.message || 'Failed to send message');
        }
    };

    const handleSavePrescription = async () => {
        try {
            const res = await consultationAPI.createPrescription(consultationId, {
                medicines,
                advice,
                diagnosis
            });
            if (res.data?.success) {
                showSuccess('Recommendations saved and sent to user');
                setShowPrescriptionModal(false);
            }
        } catch (error) {
            showError('Failed to save recommendations');
        }
    };

    const addMedicine = () => {
        setMedicines([...medicines, { name: '', dosage: '', frequency: '', duration: '' }]);
    };

    const fetchPrescription = async () => {
        try {
            const res = await consultationAPI.getPrescription(consultationId);
            if (res.data?.success) {
                setPrescription(res.data.data);
                if (!isPatient) {
                    setDiagnosis(res.data.data.diagnosis || '');
                    setAdvice(res.data.data.advice || '');
                    setMedicines(res.data.data.medicines || []);
                }
            }
        } catch (error) {
            console.error('No prescription found or failed to fetch');
        }
    };

    const handleOpenPrescription = () => {
        fetchPrescription();
        setShowPrescriptionModal(true);
    };

    const printPrescription = () => {
        const printContent = document.getElementById('printable-prescription');
        if (!printContent) return;

        window.print();
    };

    const canSendMessage = () => {
        if (isPatient) return messageLimits.patientCount < 10;
        return messageLimits.providerCount < 10;
    };

    return (
        <div className="flex flex-col h-[calc(100vh-100px)] bg-gray-900">
            <style>
                {`
                    @media print {
                        body * {
                            visibility: hidden !important;
                        }
                        #printable-prescription, #printable-prescription * {
                            visibility: visible !important;
                        }
                        #printable-prescription {
                            position: absolute !important;
                            left: 0 !important;
                            top: 0 !important;
                            width: 100% !important;
                            display: block !important;
                            background: white !important;
                            color: black !important;
                        }
                        .no-print {
                            display: none !important;
                        }
                    }
                `}
            </style>
            {/* Header */}
            <div className="bg-gray-800 p-4 border-b border-gray-700 flex justify-between items-center">
                <div className="flex items-center space-x-4">
                    <h2 className="text-xl font-semibold text-gold">Session Chat</h2>
                    {!isPatient && (
                        <div className="flex space-x-2">
                            <button
                                onClick={() => setShowHistoryModal(true)}
                                className="bg-blue-600/20 text-blue-400 border border-blue-600/30 px-3 py-1 rounded text-sm hover:bg-blue-600/30 transition-colors"
                            >
                                Detailed Record
                            </button>
                            <button
                                onClick={handleOpenPrescription}
                                className="bg-green-600/20 text-green-400 border border-green-600/30 px-3 py-1 rounded text-sm hover:bg-green-600/30 transition-colors"
                            >
                                Recommendations
                            </button>
                        </div>
                    )}
                    {isPatient && consultation?.diagnosis && (
                        <button
                            onClick={handleOpenPrescription}
                            className="bg-green-600/20 text-green-400 border border-green-600/30 px-3 py-1 rounded text-sm hover:bg-green-600/30 transition-colors"
                        >
                            View Recommendations
                        </button>
                    )}
                </div>
                <button
                    onClick={() => {
                        if (isPatient) navigate('/patient/consultation');
                        else if (user?.role === 'doctor') navigate('/dashboard/doctor/consultation');
                        else navigate('/dashboard');
                    }}
                    className="text-gray-400 hover:text-white"
                >
                    Exit Chat
                </button>
            </div>

            {/* Messages Area */}
            <div className="flex-1 overflow-y-auto p-4 space-y-4">
                {loading ? (
                    <p className="text-center text-gray-500">Loading messages...</p>
                ) : messages.length === 0 ? (
                    <p className="text-center text-gray-500">No messages yet. Start the conversation.</p>
                ) : (
                    messages.map((msg, index) => {
                        const isMe = msg.sender_id === user?.id || msg.sender_id === user?._id;
                        return (
                            <div key={index} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                                <div className={`max-w-[70%] rounded-lg p-3 ${isMe ? 'bg-gold text-dark' : 'bg-gray-700 text-white'}`}>
                                    <p className="text-sm">{msg.content}</p>
                                    <span className="text-xs opacity-70 block text-right mt-1">
                                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                </div>
                            </div>
                        );
                    })
                )}
                <div ref={messagesEndRef} />
            </div>

            {/* Input Area */}
            <div className="bg-gray-800 p-4 border-t border-gray-700">
                <div className="text-xs text-gray-400 mb-2 text-right">
                    {isPatient ? `${10 - messageLimits.patientCount} messages remaining` : `${10 - messageLimits.providerCount} messages remaining`} | {500 - newMessage.length} chars remaining
                </div>
                <form onSubmit={handleSendMessage} className="flex gap-2">
                    <input
                        type="text"
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        placeholder={canSendMessage() ? "Type your message..." : "Message limit reached"}
                        className="flex-1 bg-gray-700 text-white border border-gray-600 rounded-lg px-4 py-2 focus:outline-none focus:border-gold"
                        disabled={!canSendMessage()}
                        maxLength={500}
                    />
                    <button
                        type="submit"
                        disabled={!newMessage.trim() || !canSendMessage()}
                        className="bg-gold text-dark px-6 py-2 rounded-lg font-medium hover:bg-yellow-500 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                        Send
                    </button>
                </form>
            </div>

            {/* Modals */}
            {showPrescriptionModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[100]">
                    <div className="bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] flex flex-col">
                        <div className="p-4 border-b border-gray-700 flex justify-between items-center no-print">
                            <h3 className="text-lg font-bold text-gold">
                                {isPatient ? 'Recommendations' : 'Add Recommendations'}
                            </h3>
                            <div className="flex items-center space-x-2">
                                {prescription && (
                                    <button
                                        onClick={printPrescription}
                                        className="text-xs bg-gray-700 text-light px-2 py-1 rounded hover:bg-gray-600"
                                    >
                                        Print/PDF
                                    </button>
                                )}
                                <button onClick={() => setShowPrescriptionModal(false)} className="text-gray-400 hover:text-white">✕</button>
                            </div>
                        </div>

                        <div className="p-6 overflow-y-auto space-y-4">
                            {!isPatient ? (
                                <>
                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">Assessment</label>
                                        <textarea
                                            value={diagnosis}
                                            onChange={(e) => setDiagnosis(e.target.value)}
                                            className="w-full bg-gray-900 rounded p-2 text-sm text-white"
                                            placeholder="Enter assessment..."
                                        />
                                    </div>

                                    <div>
                                        <label className="block text-xs text-gray-400 mb-2">Products</label>
                                        {medicines.map((med, idx) => (
                                            <div key={idx} className="grid grid-cols-2 gap-2 mb-2 p-2 bg-gray-900 rounded">
                                                <input
                                                    placeholder="Product name"
                                                    value={med.name}
                                                    onChange={(e) => {
                                                        const newMeds = [...medicines];
                                                        newMeds[idx].name = e.target.value;
                                                        setMedicines(newMeds);
                                                    }}
                                                    className="bg-transparent border-b border-gray-700 text-xs text-white pb-1"
                                                />
                                                <input
                                                    placeholder="Dosage (e.g. 500mg)"
                                                    value={med.dosage}
                                                    onChange={(e) => {
                                                        const newMeds = [...medicines];
                                                        newMeds[idx].dosage = e.target.value;
                                                        setMedicines(newMeds);
                                                    }}
                                                    className="bg-transparent border-b border-gray-700 text-xs text-white pb-1"
                                                />
                                                <input
                                                    placeholder="Frequency (e.g. 1-0-1)"
                                                    value={med.frequency}
                                                    onChange={(e) => {
                                                        const newMeds = [...medicines];
                                                        newMeds[idx].frequency = e.target.value;
                                                        setMedicines(newMeds);
                                                    }}
                                                    className="bg-transparent border-b border-gray-700 text-xs text-white pb-1"
                                                />
                                                <input
                                                    placeholder="Duration (e.g. 5 days)"
                                                    value={med.duration}
                                                    onChange={(e) => {
                                                        const newMeds = [...medicines];
                                                        newMeds[idx].duration = e.target.value;
                                                        setMedicines(newMeds);
                                                    }}
                                                    className="bg-transparent border-b border-gray-700 text-xs text-white pb-1"
                                                />
                                            </div>
                                        ))}
                                        <button onClick={addMedicine} className="text-xs text-gold border border-gold/30 px-2 py-1 rounded mt-1">
                                            + Add Another
                                        </button>
                                    </div>

                                    <div>
                                        <label className="block text-xs text-gray-400 mb-1">Additional Advice</label>
                                        <textarea
                                            value={advice}
                                            onChange={(e) => setAdvice(e.target.value)}
                                            className="w-full bg-gray-900 rounded p-2 text-sm text-white"
                                            placeholder="General advice to user..."
                                        />
                                    </div>
                                </>
                            ) : (
                                <div className="space-y-4">
                                    <div className="bg-gray-900 p-4 rounded-lg border border-gold/20">
                                        <h4 className="text-gold font-bold text-sm mb-1">Assessment</h4>
                                        <p className="text-white text-sm italic">"{consultation?.diagnosis || 'Pending'}"</p>
                                    </div>

                                    <div>
                                        <h4 className="text-gray-400 text-xs font-bold uppercase tracking-wider mb-2">Recommended Products</h4>
                                        <div className="space-y-2">
                                            {prescription?.medicines?.length > 0 ? prescription.medicines.map((m, idx) => (
                                                <div key={idx} className="bg-gray-900 p-3 rounded">
                                                    <p className="font-bold text-light">{m.name}</p>
                                                    <div className="flex justify-between text-xs text-gray-400 mt-1">
                                                        <span>{m.dosage}</span>
                                                        <span>{m.frequency}</span>
                                                        <span>{m.duration}</span>
                                                    </div>
                                                </div>
                                            )) : (
                                                <p className="text-gray-500 text-xs italic">No products recommended yet.</p>
                                            )}
                                        </div>
                                    </div>
                                    {prescription?.advice && (
                                        <div className="bg-gray-900 p-3 rounded mt-4">
                                            <span className="text-xs text-gray-500 block">Provider's Advice:</span>
                                            <p className="text-sm text-white">{prescription.advice}</p>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>

                        {!isPatient && (
                            <div className="p-4 border-t border-gray-700">
                                <button
                                    onClick={handleSavePrescription}
                                    className="w-full bg-gold text-dark py-3 rounded-lg font-bold"
                                >
                                    Finalize & Send Recommendations
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            )}

            {showHistoryModal && (
                <div className="fixed inset-0 bg-black/80 flex items-center justify-center p-4 z-[100]">
                    <div className="bg-gray-800 rounded-xl max-w-lg w-full max-h-[90vh] flex flex-col">
                        <div className="p-4 border-b border-gray-700 flex justify-between items-center">
                            <h3 className="text-lg font-bold text-blue-400">User Detailed History</h3>
                            <button onClick={() => setShowHistoryModal(false)} className="text-gray-400 hover:text-white">✕</button>
                        </div>
                        <div className="p-6 overflow-y-auto space-y-4">
                            <div className="bg-gray-900 p-3 rounded">
                                <span className="text-xs text-gray-500 block">Reason for Visit:</span>
                                <p className="text-white">{consultation?.reason_for_visit || 'General Session'}</p>
                            </div>
                            <div className="bg-gray-900 p-3 rounded">
                                <span className="text-xs text-gray-500 block">Reported Symptoms:</span>
                                <p className="text-white">{consultation?.symptoms || 'None reported'}</p>
                            </div>
                            <hr className="border-gray-700" />
                            <div className="grid grid-cols-2 gap-4">
                                <div className="bg-gray-900 p-3 rounded">
                                    <span className="text-xs text-gray-500 block">Blood Group:</span>
                                    <p className="text-white">{consultation?.patient_id?.health_profile?.blood_group || 'Unknown'}</p>
                                </div>
                                <div className="bg-gray-900 p-3 rounded">
                                    <span className="text-xs text-gray-500 block">Allergies:</span>
                                    <p className="text-red-400">{consultation?.patient_id?.health_profile?.allergies?.join(', ') || 'None reported'}</p>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Printable Prescription Layout (Hidden in UI, visible in Print) */}
            <div id="printable-prescription" className="hidden bg-white p-12 text-black min-h-screen">
                <div className="flex justify-between border-b-4 border-gold pb-6 mb-8">
                    <div>
                        <h1 className="text-3xl font-extrabold text-gold tracking-tight">VIVARA</h1>
                        <p className="text-sm font-bold uppercase tracking-widest text-gray-500">Official Digital Recommendations</p>
                    </div>
                    <div className="text-right">
                        <p className="text-xl font-bold">{consultation?.doctor_id?.name || 'Practitioner'}</p>
                        <p className="text-sm text-gray-600">{consultation?.doctor_id?.specialization || 'Expert Provider'}</p>
                        <p className="text-xs text-gray-500">Vivara Network</p>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-12 mb-10 text-sm">
                    <div className="bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-bold text-gray-400 uppercase text-xs mb-3 border-b pb-1">User Details</h3>
                        <p className="text-lg font-semibold">{consultation?.patient_id?.name}</p>
                        <div className="flex space-x-4 mt-1 text-gray-600">
                            <span>Age: {consultation?.patient_id?.age || 'N/A'}</span>
                            <span>Gender: {consultation?.patient_id?.gender || 'N/A'}</span>
                        </div>
                    </div>
                    <div className="text-right bg-gray-50 p-4 rounded-lg">
                        <h3 className="font-bold text-gray-400 uppercase text-xs mb-3 border-b pb-1 text-right">Session Summary</h3>
                        <p><span className="text-gray-500">Date:</span> <span className="font-semibold">{new Date(prescription?.createdAt || Date.now()).toLocaleDateString('en-IN', { day: 'numeric', month: 'long', year: 'numeric' })}</span></p>
                        <p><span className="text-gray-500">Ref ID:</span> <span className="font-mono font-semibold">#{consultationId?.slice(-8).toUpperCase()}</span></p>
                    </div>
                </div>

                <div className="mb-10">
                    <h3 className="font-bold text-gray-400 uppercase text-xs mb-4 border-b-2 border-gray-100 pb-2 flex items-center">
                        <span className="bg-gold text-dark px-2 py-0.5 rounded text-[10px] mr-2">AS</span> Provisional Assessment
                    </h3>
                    <p className="text-xl text-gray-800 font-medium leading-relaxed italic">"{prescription?.diagnosis || 'General Session'}"</p>
                </div>

                <div className="mb-10">
                    <h3 className="font-bold text-gray-400 uppercase text-xs mb-4 border-b-2 border-gray-100 pb-2 flex items-center">
                        <span className="bg-gold text-dark px-2 py-0.5 rounded text-[10px] mr-2">RE</span> Recommended Regimen
                    </h3>
                    <table className="w-full text-left border-collapse">
                        <thead>
                            <tr className="bg-gray-100 text-gray-700 text-xs uppercase">
                                <th className="py-3 px-4 rounded-l-lg">Product & Dosage</th>
                                <th className="py-3 px-4">Frequency</th>
                                <th className="py-3 px-4 rounded-r-lg">Duration</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                            {prescription?.medicines?.map((m, idx) => (
                                <tr key={idx}>
                                    <td className="py-4 px-4">
                                        <p className="font-bold text-gray-900">{m.name}</p>
                                        <p className="text-xs text-gray-500">{m.dosage || 'As directed'}</p>
                                    </td>
                                    <td className="py-4 px-4 text-gray-700 font-medium">{m.frequency}</td>
                                    <td className="py-4 px-4 text-gray-700 font-medium">{m.duration}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>

                <div className="mb-12 bg-blue-50/50 p-6 rounded-xl border border-blue-100">
                    <h3 className="font-bold text-blue-800 uppercase text-xs mb-3 flex items-center">
                        <span className="mr-2">📝</span> Provider's Advice & Instructions
                    </h3>
                    <p className="text-gray-800 leading-relaxed whitespace-pre-wrap">{prescription?.advice || 'Maintain hydration and follow-up if symptoms persist.'}</p>
                </div>

                <div className="mt-auto pt-16 flex justify-between items-end border-t border-gray-200">
                    <div className="text-[10px] text-gray-400 max-w-[300px]">
                        <p>This is a computer-generated document and is valid only within the Vivara Network. Please consult a physical practitioner for emergencies.</p>
                        <p className="mt-1">© Vivara 2026. All rights reserved.</p>
                    </div>
                    <div className="text-right">
                        <div className="inline-block pt-2 min-w-[200px]">
                            <p className="text-xs text-gray-400 uppercase tracking-tighter mb-1">Signed By</p>
                            <p className="font-bold text-lg leading-tight">{consultation?.doctor_id?.name}</p>
                            <p className="text-[10px] text-gold font-bold">DIGITALLY VERIFIED</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ChatRoom;
